import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { citationsApi } from '@/api';
import { formatDateTime } from '@/utils/dateFormatting';
import type { CitationLinkResponse, CitationLinkTargetType, CitationResponse } from 'we-tree-api-client/v2';
import ImageEnlargeOverlay from '@/components/shared/ImageEnlargeOverlay';
import Info from '@/icons/Info';

type FactOrLifeEventTargetType = Extract<CitationLinkTargetType, 'person_fact' | 'life_event'>;

import './record-info-popover.css';

interface RecordInfoPopoverProps {
  createdAt: string;
  updatedAt: string;
  familyId: string;
  targetType: FactOrLifeEventTargetType;
  targetId: string;
  /** Optional class for the trigger wrapper (e.g. person-facts vs person-life-events) */
  triggerClassName?: string;
  ariaLabel?: string;
  /** Fact: notes and source notes. Life event: detail, description, sourceNotes. Rendered in popover when present. */
  notes?: string | null;
  sourceNotes?: string | null;
  detail?: string | null;
  description?: string | null;
  /** Display name for who created / last updated the record. */
  createdByNickname?: string | null;
  updatedByNickname?: string | null;
}

const VIEWPORT_PAD = 8;

/**
 * Single info popover: record metadata (added/updated), optional details (notes/source/detail/description),
 * and "View image" for any linked citation that has media.
 */
const RecordInfoPopover: React.FC<RecordInfoPopoverProps> = ({
  createdAt,
  updatedAt,
  familyId,
  targetType,
  targetId,
  triggerClassName = 'record-info-popover__trigger',
  ariaLabel,
  notes,
  sourceNotes,
  detail,
  description,
  createdByNickname,
  updatedByNickname
}) => {
  const { t } = useTranslation('common');
  const { t: tForms } = useTranslation('forms');
  const resolvedAriaLabel = ariaLabel ?? tForms('person.recordInfo');
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState<CitationLinkResponse[]>([]);
  const [citationDetails, setCitationDetails] = useState<Record<string, CitationResponse>>({});
  const [loadingCitations, setLoadingCitations] = useState(false);
  const [imageOverlayMediaId, setImageOverlayMediaId] = useState<string | null>(null);

  const loadCitationLinksAndDetails = useCallback(async () => {
    setLoadingCitations(true);
    const linksResult = await citationsApi.listCitationLinksByTarget(familyId, targetType, targetId);
    setLoadingCitations(false);
    if (!linksResult.success || !linksResult.data?.items?.length) {
      setLinks([]);
      setCitationDetails({});
      return;
    }
    const linkList = linksResult.data.items;
    setLinks(linkList);
    const results = await Promise.all(linkList.map((link) => citationsApi.getCitation(familyId, link.citationId)));
    const details: Record<string, CitationResponse> = {};
    results.forEach((result, i) => {
      if (result.success && result.data) {
        details[linkList[i].citationId] = result.data;
      }
    });
    setCitationDetails(details);
  }, [familyId, targetType, targetId]);

  useEffect(() => {
    if (open) {
      loadCitationLinksAndDetails();
    }
  }, [open, loadCitationLinksAndDetails]);

  const mediaAssetIds = React.useMemo(() => {
    const ids: string[] = [];
    links.forEach((link) => {
      const cit = citationDetails[link.citationId] as (CitationResponse & { mediaAssetId?: string }) | undefined;
      if (cit?.mediaAssetId && !ids.includes(cit.mediaAssetId)) {
        ids.push(cit.mediaAssetId);
      }
    });
    return ids;
  }, [links, citationDetails]);

  const handleViewImage = (mediaAssetId: string) => {
    setImageOverlayMediaId(mediaAssetId);
  };

  const showUpdated = createdAt !== updatedAt;
  const addedFormatted = formatDateTime(createdAt);
  const updatedFormatted = formatDateTime(updatedAt);
  const hasDetails =
    (notes != null && notes !== '') ||
    (sourceNotes != null && sourceNotes !== '') ||
    (detail != null && detail !== '') ||
    (description != null && description !== '');

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div className="record-info-popover">
        <Popover.Trigger asChild>
          <button type="button" className={triggerClassName} aria-label={resolvedAriaLabel}>
            <span className="record-info-popover__trigger-icon" aria-hidden>
              <Info width={14} height={14} />
            </span>
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className="record-info-popover__panel"
            side="bottom"
            align="end"
            sideOffset={8}
            avoidCollisions
            collisionPadding={VIEWPORT_PAD}
          >
            <div className="record-info-popover__section">
              <div className="record-info-popover__row">
                <strong>{t('meta.added')}</strong>
                <span>
                  {addedFormatted}
                  {createdByNickname && createdByNickname.trim() !== ''
                    ? `${t('meta.by')}${createdByNickname.trim()}`
                    : ''}
                </span>
              </div>
              {showUpdated && (
                <div className="record-info-popover__row">
                  <strong>{t('meta.lastUpdated')}</strong>
                  <span>
                    {updatedFormatted}
                    {updatedByNickname && updatedByNickname.trim() !== ''
                      ? `${t('meta.by')}${updatedByNickname.trim()}`
                      : ''}
                  </span>
                </div>
              )}
            </div>
            {hasDetails && (
              <div className="record-info-popover__section record-info-popover__details">
                {notes != null && notes !== '' && (
                  <div className="record-info-popover__detail-block">
                    <strong>{tForms('recordInfo.notes')}:</strong>
                    <div className="record-info-popover__detail-text">{notes}</div>
                  </div>
                )}
                {sourceNotes != null && sourceNotes !== '' && (
                  <div className="record-info-popover__detail-block">
                    <strong>{tForms('recordInfo.source')}:</strong>
                    <div className="record-info-popover__detail-text">{sourceNotes}</div>
                  </div>
                )}
                {detail != null && detail !== '' && (
                  <div className="record-info-popover__detail-block">
                    <strong>{tForms('recordInfo.details')}:</strong>
                    <div className="record-info-popover__detail-text">{detail}</div>
                  </div>
                )}
                {description != null && description !== '' && (
                  <div className="record-info-popover__detail-block">
                    <strong>{tForms('recordInfo.description')}:</strong>
                    <div className="record-info-popover__detail-text">{description}</div>
                  </div>
                )}
              </div>
            )}
            {loadingCitations ? (
              <div className="record-info-popover__loading">{tForms('evidence.loadingCitations')}</div>
            ) : mediaAssetIds.length > 0 ? (
              <div className="record-info-popover__section record-info-popover__media">
                <span className="record-info-popover__media-label">{tForms('recordInfo.citationImage')}</span>
                <div className="record-info-popover__media-actions">
                  {mediaAssetIds.map((mediaAssetId) => (
                    <button
                      key={mediaAssetId}
                      type="button"
                      className="record-info-popover__view-image"
                      onClick={() => handleViewImage(mediaAssetId)}
                    >
                      {tForms('citations.viewImage')}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </Popover.Content>
        </Popover.Portal>

        {familyId && imageOverlayMediaId && (
          <ImageEnlargeOverlay
            isOpen
            onClose={() => setImageOverlayMediaId(null)}
            familyId={familyId}
            mediaAssetId={imageOverlayMediaId}
          />
        )}
      </div>
    </Popover.Root>
  );
};

export default RecordInfoPopover;
